import Base from '../Base/Base';
import styles from './ApplicationMaintenance.module.scss';
import { theme } from '../../components/layouts/theme';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import AppManagementModel, {
	AppMaster,
} from '../../Classes/ResponseModels/ApplicationManagement/AppManagementModel';
import Section from './components/NewApplication/Section';
import AppMasterForm from './components/NewApplication/AppForms/AppMasterForm';
import AppAuxForm from './components/NewApplication/AppForms/AppAuxForm';
import UPMCApplicationForm from './components/NewApplication/AppForms/UPMCApplicationForm';
import { Button } from '@upmc/ims.component-library';
import MaterialIcon from '@material/react-material-icon';
import { CreateIMSApp } from './_api';
import NewAppRequest from '../../Classes/ResponseModels/ApplicationManagement/NewAppRequest';
import { useHistory } from 'react-router-dom';
import ApiErrors from './components/NewApplication/ApiErrors';
import ReqNumModal from './components/ReqNumModal';
import classNames from 'classnames';
import WorkQueTypeSelect from './components/WorkQueTypeSelect/WorkQueTypeSelect';
import ServiceAccountForm from './components/NewApplication/AppForms/ServiceAccountForm';

const NewApplication = ({ currentUser }) => {
	const [newApp, setNewApp] = useState(new AppManagementModel());
	const [workQueType, setWorkQueType] = useState(null);
	const [systemID, setSystemID] = useState(null);
	const [systemName, setSystemName] = useState(null);
	const [apiErrors, setApiErrors] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const history = useHistory();
	//client side validation errors
	const [appMasterErrors, setAppMasterErrors] = useState([]);
	const [requirementNumber, setRequirementNumber] = useState(null);
	const [reqNumModalOpen, setReqNumModalOpen] = useState(true);
	//when newApp changes, run client side validations and set corresponding state variables
	//disable save button if any validation errors are present
	useEffect(() => {
		setAppMasterErrors(AppMaster.validate(newApp.appMaster));
	}, [newApp]);

	const handleSave = () => {
		//if there are no client side errors, submit the request. if there are errors, do not allow submission
		if (appMasterErrors.length === 0) {
			const request = new NewAppRequest(newApp, currentUser);
			request.workQueType = workQueType;
			request.RequirementNumber = requirementNumber;
			request.systemID = systemID;
			//if systemName is not null or empty, assign systemName value as the system name in the request body. else default to app name
			request.systemName =
				systemName !== null && systemName !== ''
					? systemName
					: newApp.appMaster.name;
			setIsSaving(true);
			setApiErrors([]);
			CreateIMSApp(request)
				.then(response => {
					//if errors present, display the errors and re render the form true to the data saved
					//CreateIMSApp returns a new AppManagementModel with data pulled from database for this purpose
					//if an error is present, the data that was saved to the db is pulled and sent back to populate the form, AND THEN the records are removed from the db to allow for resubmission of the form
					if (response?.errors?.length > 0) {
						setNewApp(response);
						setApiErrors(response.errors);
					}
					// if no errors, redirect user to the dashboard with the new app pre selected
					else {
						history.push(
							`/ApplicationMaintenance/?appID=${response.appMaster.id}`
						);
					}
				})
				.then(x => setIsSaving(false));
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Base
				title='New Application'
				loading={isSaving}
				className={styles.APPLY_FONT}
			>
				<ReqNumModal
					reqNum={requirementNumber}
					setReqNum={setRequirementNumber}
					open={reqNumModalOpen}
					setOpen={setReqNumModalOpen}
					omitReadOnly={true}
				/>
				<div className={styles.controlsContainer}>
					<Button
						onClick={e => {
							handleSave();
						}}
						outlined
						startIcon={<MaterialIcon icon='save' />}
						className={styles.editButton}
						disabled={
							appMasterErrors.length > 0 ||
							requirementNumber === null
						}
					>
						Save
					</Button>
				</div>
				<div className={classNames([styles.text_center, styles.row])}>
					{requirementNumber === null ? (
						<div>
							<strong>Read Only</strong>
						</div>
					) : (
						<div>
							<strong>
								Requirement Number: {requirementNumber}
							</strong>
						</div>
					)}
					<Button
						onClick={() => setReqNumModalOpen(true)}
						sx={{ m: '1rem' }}
						variant='outlined'
					>
						<MaterialIcon icon='edit' />
					</Button>

					{!requirementNumber ? (
						<p className={styles.error}>
							A valid requirement number is required to save
						</p>
					) : null}
				</div>
				<ApiErrors errors={apiErrors} />
				<Section label='Application Master'>
					<AppMasterForm
						app={newApp}
						setApp={setNewApp}
						validationErrors={appMasterErrors}
					/>
				</Section>
				<Section label='Application Auxiliary'>
					<AppAuxForm app={newApp} setApp={setNewApp} />
				</Section>
				<Section label='Application Information (UPMC Application)'>
					<UPMCApplicationForm app={newApp} setApp={setNewApp} />
				</Section>
				<Section label='Work Queue'>
					<WorkQueTypeSelect
						workQueType={workQueType}
						setWorkQueType={setWorkQueType}
						systemID={systemID}
						setSystemID={setSystemID}
						systemName={systemName}
						setSystemName={setSystemName}
					/>
				</Section>
				<Section label='Service Account'>
					<ServiceAccountForm app={newApp} setApp={setNewApp} />
				</Section>
				<div className={styles.controlsContainer}>
					<Button
						onClick={e => {
							handleSave();
						}}
						startIcon={<MaterialIcon icon='save' />}
						className={styles.editButton}
						disabled={appMasterErrors.length > 0}
						sx={{ m: '1rem' }}
					>
						Save
					</Button>
				</div>
				<ApiErrors errors={apiErrors} />
			</Base>
		</ThemeProvider>
	);
};

export default NewApplication;
